import { defineComponent as _defineComponent } from 'vue'
import { onMounted, onUnmounted, ref, watchEffect } from "vue";
import mp4Box from "mp4box";

import ActionBox from "./components/ActionBox.vue";
import MediaControlPanel from "./components/MediaControlPanel.vue";
import TopNavbar from "./components/TopNavbar.vue";
import useDewarp from "./service/useDewarp";
import {
  ACCEPT_TYPE,
  FILE_SIZE_UNITS,
  INITIAL_VIDEO_DURATION,
  VIDEO_DEVICE_SETTINGS,
} from "./constants";
import { getConvertedJSONFromBufferArray } from "./utils";
import { FileData } from "./types";

interface FileBuffer extends ArrayBuffer {
  fileStart?: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const videoPlayer = ref<HTMLVideoElement>();
const videoPlayerContainer = ref<HTMLDivElement>();
const videoCanvas = ref<HTMLCanvasElement>();
const file = ref<FileData>();
const isVideoPaused = ref(true);
const isErrorVideo = ref(false);
const videoProcessingOnLoading = ref(false);
const mediaCurrentTime = ref(INITIAL_VIDEO_DURATION);
const shouldShowInfo = ref(false);
const isFishEyeVideo = ref(true);
const videoCurrentTimeUpdateInterval = ref();

const infoDisplayTimeoutMs = 2000;
const videoCurrentTimeUpdateDelayInMS = 100;
const defaultMediaPlaybackRate = 1;
const uploadText = "Drag and drop exported video from Eagle Eye camera";
const uploadTextLine2 = "or";
const uploadButtonLabel = "Browse videos";
const wrongTypeErrorMessage = "Upload failed. Supported file types are video.";
const uploadFileErrorMessage = "Upload failed. Please try again.";
const infoCardText = "Drag to rotate, scroll to zoom.";
const portraitInfoText = "Please use landscape mode";
const errorButtonLabel = "Try again";
const errorTitle = "The video cannot be played";
const errorDescription =
  "The current video does not originate from an Eagle Eye camera";

const { dewarpVideo, setResize } = useDewarp();

const onWindowResize = () => {
  if (videoCanvas.value) {
    setResize({
      width: window.innerWidth,
      // Calculate height to keep aspect ratio of the canvas is 16/9
      height: (window.innerWidth / 16) * 9,
    });
  }
};

onMounted(() => window.addEventListener("resize", onWindowResize));

onUnmounted(() => window.removeEventListener("resize", onWindowResize));

watchEffect(() => {
  if (isVideoPaused.value) {
    clearInterval(videoCurrentTimeUpdateInterval.value);
    return;
  }

  videoCurrentTimeUpdateInterval.value = setInterval(() => {
    if (!videoPlayer.value) {
      return;
    }

    mediaCurrentTime.value = videoPlayer.value.currentTime;
  }, videoCurrentTimeUpdateDelayInMS);

});

const processVideoFile = async (file: File) => {
  if (file) {
    const mp4BoxFile = mp4Box.createFile(false);

    const fileBuffer: FileBuffer = await file.arrayBuffer();
    fileBuffer.fileStart = 0;
    mp4BoxFile.appendBuffer(fileBuffer);
    mp4BoxFile.onError = console.error;

    const udtaData = mp4BoxFile.udta;
    videoPlayer.value?.setAttribute("src", URL.createObjectURL(file));
    videoProcessingOnLoading.value = false;
    if (!udtaData?.lens?.data || !udtaData?.dcon?.data) {
      isFishEyeVideo.value = false;

      return;
    }

    try {
      const convertedLens = getConvertedJSONFromBufferArray(udtaData.lens.data);
      const convertedDcon = getConvertedJSONFromBufferArray(udtaData.dcon.data);

      const updatedVideoDeviceSettings = {
        ...VIDEO_DEVICE_SETTINGS,
        fov: convertedLens.fov,
        mount: convertedDcon.mount,
      };

      if (
        videoCanvas.value &&
        videoPlayer.value &&
        videoPlayerContainer.value
      ) {
        dewarpVideo(
          updatedVideoDeviceSettings,
          videoCanvas.value,
          videoPlayerContainer.value,
          videoPlayer.value
        );
      }
    } catch (error) {
      isErrorVideo.value = true;
    }
  }
};

const onUploadSuccess = (mediaData: FileData) => {
  videoProcessingOnLoading.value = true;
  processVideoFile(mediaData.addedFile);
  file.value = mediaData;
};

const onVideoPlay = () => {
  if (videoPlayer.value?.paused) {
    videoPlayer.value.play();
    isVideoPaused.value = false;

    return;
  }

  if (videoPlayer.value?.play) {
    videoPlayer.value?.pause();
    isVideoPaused.value = true;
  }
};

const onUpdateCurrentTime = (
  time = INITIAL_VIDEO_DURATION,
  isIncrement = true
) => {
  if (videoPlayer.value) {
    const updatedTime = isIncrement
      ? videoPlayer.value.currentTime + time
      : time;
    videoPlayer.value.currentTime = updatedTime;
  }
};

const resetValues = () => {
  isVideoPaused.value = true;
  isErrorVideo.value = false;
  videoPlayer.value?.removeAttribute("src");
  file.value = undefined;
  isFishEyeVideo.value = true;
  mediaCurrentTime.value = INITIAL_VIDEO_DURATION;
};

const handleVideoVolume = (volume: number) => {
  if (videoPlayer.value) {
    videoPlayer.value.volume = volume;
  }
};

const handleVideoSpeed = (speed: number) => {
  if (videoPlayer.value) {
    videoPlayer.value.playbackRate = speed;
  }
};

const onCanvasHover = () => {
  shouldShowInfo.value = true;

  setTimeout(() => {
    shouldShowInfo.value = false;
  }, infoDisplayTimeoutMs);
};

const onVideoEnd = () => {
  isVideoPaused.value = true;
  isErrorVideo.value = false;
  mediaCurrentTime.value = INITIAL_VIDEO_DURATION;
  if (videoPlayer.value) {
    videoPlayer.value.currentTime = INITIAL_VIDEO_DURATION;
  }
};

return { __sfc: true,videoPlayer, videoPlayerContainer, videoCanvas, file, isVideoPaused, isErrorVideo, videoProcessingOnLoading, mediaCurrentTime, shouldShowInfo, isFishEyeVideo, videoCurrentTimeUpdateInterval, infoDisplayTimeoutMs, videoCurrentTimeUpdateDelayInMS, defaultMediaPlaybackRate, uploadText, uploadTextLine2, uploadButtonLabel, wrongTypeErrorMessage, uploadFileErrorMessage, infoCardText, portraitInfoText, errorButtonLabel, errorTitle, errorDescription, dewarpVideo, setResize, onWindowResize, processVideoFile, onUploadSuccess, onVideoPlay, onUpdateCurrentTime, resetValues, handleVideoVolume, handleVideoSpeed, onCanvasHover, onVideoEnd, ActionBox, MediaControlPanel, TopNavbar, ACCEPT_TYPE, FILE_SIZE_UNITS, INITIAL_VIDEO_DURATION }
}

})